import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/storefront/Navbar";
import Footer from "@/components/storefront/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ArrowLeft, CreditCard } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { formatSAR } from "@/lib/i18n";
import { toast } from "sonner";

export default function Cart() {
  const { cart, removeFromCart, updateQty, cartSubtotal, cartVat, cartTotal, lineUnitPrice, t, lang, user } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_mobile: user?.phone || "",
    customer_email: user?.email && !user.email.includes("@beforethedeadline.local") ? user.email : "",
    customer_city: "Riyadh",
  });
  const [submitting, setSubmitting] = useState(false);
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

  const onCheckout = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error(t.cartEmpty);
      return;
    }
    if (!form.customer_name.trim() || !form.customer_mobile.trim()) {
      toast.error(lang === "ar" ? "يرجى إدخال البيانات" : "Please fill required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((c) => ({
          product_id: c.product.id,
          variant_id: c.variant_id,
          service_id: c.service_id,
          qty: c.qty,
          customer_info: c.customer_info || "",
        })),
        origin_url: window.location.origin,
        ...form,
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const onCheckoutPaypal = async () => {
    if (cart.length === 0) { toast.error(t.cartEmpty); return; }
    if (!form.customer_name.trim() || !form.customer_mobile.trim()) {
      toast.error(lang === "ar" ? "يرجى إدخال البيانات" : "Please fill required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((c) => ({
          product_id: c.product.id,
          variant_id: c.variant_id,
          service_id: c.service_id,
          qty: c.qty,
          customer_info: c.customer_info || "",
        })),
        origin_url: window.location.origin,
        ...form,
      };
      const { data } = await api.post("/checkout/paypal", payload);
      window.location.href = data.approval_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PayPal checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
        <h1 className="text-3xl font-bold mb-8">{t.cart}</h1>

        {cart.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-16 text-center" data-testid="empty-cart-page">
            <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground mb-6">{t.cartEmpty}</p>
            <Button onClick={() => navigate("/")} className="gap-2" data-testid="continue-shopping">
              {t.continueShopping}
              <Arrow className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <ul className="lg:col-span-2 space-y-4">
              {cart.map((item) => {
                const p = item.product;
                const name = lang === "ar" ? p.name_ar : p.name_en;
                const variant = (p.variants || []).find((v) => v.id === item.variant_id);
                const service = (p.service_options || []).find((s) => s.id === item.service_id);
                const variantName = variant ? (lang === "ar" ? variant.name_ar : variant.name_en) : null;
                const serviceName = service ? (lang === "ar" ? service.name_ar : service.name_en) : null;
                const unitPrice = lineUnitPrice(item);
                const img = variant?.preview_image || p.preview_image;
                return (
                  <li
                    key={`${p.id}-${item.variant_id}-${item.service_id}`}
                    className="bg-surface border border-border rounded-xl p-4 flex gap-4"
                    data-testid={`cart-page-item-${p.id}`}
                  >
                    <img src={img} alt={name} className="h-28 w-24 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold">{name}</p>
                      <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                        {variantName && <p>{t.style}: {variantName}</p>}
                        {serviceName && <p>{t.serviceType}: {serviceName}</p>}
                      </div>
                      <p className="text-sm font-medium mt-2">{formatSAR(unitPrice, lang)}</p>
                      <div className="mt-3 flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQty(p.id, item.variant_id, item.service_id, item.qty - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-sm">{item.qty}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQty(p.id, item.variant_id, item.service_id, item.qty + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 ms-2 text-muted-foreground hover:text-destructive"
                          onClick={() => removeFromCart(p.id, item.variant_id, item.service_id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="font-bold text-end whitespace-nowrap">
                      {formatSAR(unitPrice * item.qty, lang)}
                    </div>
                  </li>
                );
              })}
            </ul>

            <form
              onSubmit={onCheckout}
              className="bg-surface border border-border rounded-xl p-6 h-fit lg:sticky lg:top-24 space-y-5"
              data-testid="checkout-form"
            >
              <h2 className="font-bold text-lg">{t.checkoutDetails}</h2>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name" className="text-xs">{t.customerName} *</Label>
                  <Input
                    id="name"
                    data-testid="checkout-name"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="mobile" className="text-xs">{t.customerMobile} *</Label>
                  <Input
                    id="mobile"
                    data-testid="checkout-mobile"
                    placeholder="+9665..."
                    value={form.customer_mobile}
                    onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="text-xs">{t.customerCity}</Label>
                  <Input
                    id="city"
                    data-testid="checkout-city"
                    value={form.customer_city}
                    onChange={(e) => setForm({ ...form, customer_city: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs">{t.customerEmail}</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="checkout-email"
                    value={form.customer_email}
                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2 pt-3 border-t border-border">
                <Row label={t.subtotal} value={formatSAR(cartSubtotal, lang)} />
                <Row label={t.vat} value={formatSAR(cartVat, lang)} />
                <Row label={t.total} value={formatSAR(cartTotal, lang)} bold />
              </div>
              <Button
                type="submit"
                size="lg"
                className="w-full gap-2"
                disabled={submitting}
                data-testid="checkout-submit"
              >
                <CreditCard className="h-4 w-4" />
                {submitting ? "..." : t.payWithStripe}
              </Button>
              <Button
                type="button"
                onClick={onCheckoutPaypal}
                size="lg"
                variant="outline"
                className="w-full gap-2 border-[#FFC439] text-[#003087] hover:bg-[#FFC439]/10 font-bold"
                disabled={submitting}
                data-testid="checkout-paypal"
              >
                <span className="font-extrabold">Pay</span><span className="text-[#009cde] font-extrabold">Pal</span>
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                Stripe · Apple Pay · Mada · Visa · Mastercard · PayPal
              </p>
            </form>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between text-sm ${bold ? "text-base font-bold pt-2 border-t border-border" : ""}`}>
    <span className={bold ? "" : "text-muted-foreground"}>{label}</span>
    <span>{value}</span>
  </div>
);
